import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Package } from "lucide-react";

const BulkVariantUpdateModal = ({ isOpen, onClose, onApply, variantCount }) => {
  const [formData, setFormData] = useState({
    price_regular: "",
    price_sale: "",
    stock: "",
    weight_grams: "",
    is_active: null, // null means don't change, true/false means set to that value
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate price_regular if provided
    if (formData.price_regular !== "") {
      const price = parseFloat(formData.price_regular);
      if (isNaN(price) || price < 0) {
        newErrors.price_regular = "Regular price must be a valid positive number";
      }
    }

    // Validate price_sale if provided
    if (formData.price_sale !== "") {
      const salePrice = parseFloat(formData.price_sale);
      if (isNaN(salePrice) || salePrice < 0) {
        newErrors.price_sale = "Sale price must be a valid positive number";
      }
      // If both prices are provided, sale should be equal to or less than regular
      if (formData.price_regular !== "") {
        const regularPrice = parseFloat(formData.price_regular);
        if (!isNaN(regularPrice) && salePrice > regularPrice) {
          newErrors.price_sale =
            "Sale price should be equal to or less than regular price";
        }
      }
    }

    // Validate stock if provided
    if (formData.stock !== "") {
      const stock = parseInt(formData.stock);
      if (isNaN(stock) || stock < 0) {
        newErrors.stock = "Stock must be a valid non-negative integer";
      }
    }

    // Validate weight_grams if provided
    if (formData.weight_grams !== "") {
      const weight = parseInt(formData.weight_grams);
      if (isNaN(weight) || weight < 0) {
        newErrors.weight_grams = "Weight must be a valid non-negative integer";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!validateForm()) {
      return;
    }

    // Prepare update data - only include fields that have values
    const updateData = {};

    if (formData.price_regular !== "" && formData.price_regular !== null) {
      const price = parseFloat(formData.price_regular);
      if (!isNaN(price)) {
        updateData.price_regular = price;
      }
    }

    if (formData.price_sale !== "" && formData.price_sale !== null) {
      const salePrice = parseFloat(formData.price_sale);
      if (!isNaN(salePrice)) {
        updateData.price_sale = salePrice;
      }
    }

    if (formData.stock !== "" && formData.stock !== null) {
      const stock = parseInt(formData.stock);
      if (!isNaN(stock)) {
        updateData.stock = stock;
      }
    }

    if (formData.weight_grams !== "" && formData.weight_grams !== null) {
      const weight = parseInt(formData.weight_grams);
      if (!isNaN(weight)) {
        updateData.weight_grams = weight;
      }
    }

    if (formData.is_active !== null) {
      updateData.is_active = formData.is_active;
    }

    // Only apply if at least one field is set
    if (Object.keys(updateData).length === 0) {
      setErrors({ general: "Please fill at least one field to update" });
      return;
    }

    console.log("Bulk update data being sent:", updateData);
    
    // Validate that onApply is a function
    if (typeof onApply !== "function") {
      console.error("onApply is not a function:", onApply);
      setErrors({ general: "Update function is not available. Please refresh the page." });
      return;
    }
    
    // Apply the update first, then close
    try {
      onApply(updateData);
      // Close the modal and reset form
      handleClose();
    } catch (error) {
      console.error("Error applying bulk update:", error);
      setErrors({ general: "Failed to apply updates. Please try again." });
    }
  };

  const handleClose = () => {
    setFormData({
      price_regular: "",
      price_sale: "",
      stock: "",
      weight_grams: "",
      is_active: null,
    });
    setErrors({});
    onClose();
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div 
        className="relative bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Set All Variants
              </h3>
              <p className="text-sm text-gray-600">
                Update common fields for all {variantCount} variant
                {variantCount !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form 
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(e);
          }} 
          className="p-6 space-y-6"
          noValidate
        >
          {/* General Error */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {errors.general}
            </div>
          )}

          {/* Info Note */}
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
            <p>
              <strong>Note:</strong> Only fill the fields you want to update.
              Leave fields empty to keep their current values. SKU, attributes,
              and default status will not be changed.
            </p>
          </div>

          {/* Regular Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Regular Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.price_regular}
              onChange={(e) => handleInputChange("price_regular", e.target.value)}
              placeholder="Leave empty to keep current"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.price_regular ? "border-red-300" : "border-gray-300"
              }`}
            />
            {errors.price_regular && (
              <p className="text-red-500 text-xs mt-1">{errors.price_regular}</p>
            )}
          </div>

          {/* Sale Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sale Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.price_sale}
              onChange={(e) => handleInputChange("price_sale", e.target.value)}
              placeholder="Leave empty to keep current"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.price_sale ? "border-red-300" : "border-gray-300"
              }`}
            />
            {errors.price_sale && (
              <p className="text-red-500 text-xs mt-1">{errors.price_sale}</p>
            )}
          </div>

          {/* Stock */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stock Quantity
            </label>
            <input
              type="number"
              min="0"
              value={formData.stock}
              onChange={(e) => handleInputChange("stock", e.target.value)}
              placeholder="Leave empty to keep current"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.stock ? "border-red-300" : "border-gray-300"
              }`}
            />
            {errors.stock && (
              <p className="text-red-500 text-xs mt-1">{errors.stock}</p>
            )}
          </div>

          {/* Weight */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Weight (grams)
            </label>
            <input
              type="number"
              min="0"
              value={formData.weight_grams}
              onChange={(e) => handleInputChange("weight_grams", e.target.value)}
              placeholder="Leave empty to keep current"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.weight_grams ? "border-red-300" : "border-gray-300"
              }`}
            />
            {errors.weight_grams && (
              <p className="text-red-500 text-xs mt-1">{errors.weight_grams}</p>
            )}
          </div>

          {/* Active Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Active Status
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="is_active"
                  checked={formData.is_active === null}
                  onChange={() => handleInputChange("is_active", null)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Keep current status
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="is_active"
                  checked={formData.is_active === true}
                  onChange={() => handleInputChange("is_active", true)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Set all active</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="is_active"
                  checked={formData.is_active === false}
                  onChange={() => handleInputChange("is_active", false)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Set all inactive
                </span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              Apply to All Variants
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default BulkVariantUpdateModal;

